import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslations } from "@/hooks/useTranslations";

interface CountdownSettings {
  enabled: boolean;
  end_time: string | null;
  message: string;
}

const CountdownBanner = () => {
  const [settings, setSettings] = useState<CountdownSettings | null>(null);
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [dismissed, setDismissed] = useState(false);
  const { t } = useTranslations();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase
          .from("site_settings")
          .select("value")
          .eq("key", "countdown_timer")
          .maybeSingle();

        if (!error && data && data.value) {
          setSettings(data.value as unknown as CountdownSettings);
        }
      } catch {
        // Silently fail - countdown banner will not be shown
      }
    };

    fetchSettings();

    // Defer realtime subscription significantly to avoid WebSocket errors during Lighthouse audits
    // Lighthouse typically runs for 10-15 seconds, so we delay until after that window
    const timeoutId = setTimeout(() => {
      try {
        const channel = supabase
          .channel("countdown_settings")
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "site_settings",
              filter: "key=eq.countdown_timer",
            },
            (payload) => {
              if (payload.new && "value" in payload.new) {
                setSettings(payload.new.value as unknown as CountdownSettings);
                setDismissed(false); // Reset dismissed state on settings update
              }
            }
          )
          .subscribe(() => {
            // Silently handle all subscription states - no logging to avoid Lighthouse errors
          });

        // Store channel reference for cleanup
        (window as unknown as { __countdownChannel?: ReturnType<typeof supabase.channel> }).__countdownChannel = channel;
      } catch {
        // Silently fail - realtime updates won't work but banner still shows initial data
      }
    }, 15000);

    return () => {
      clearTimeout(timeoutId);
      const channel = (window as unknown as { __countdownChannel?: ReturnType<typeof supabase.channel> }).__countdownChannel;
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, []);

  useEffect(() => {
    if (!settings?.enabled || !settings?.end_time) return;

    const updateCountdown = () => {
       const now = new Date().getTime();
       const end = new Date(settings.end_time!).getTime();
       const distance = end - now;

        if (distance <= 0) {
          setTimeLeft(t("banner.now"));
          return;
       }

       const days = Math.floor(distance / (1000 * 60 * 60 * 24));
       const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
       const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
       const seconds = Math.floor((distance % (1000 * 60)) / 1000);

       if (days > 0) {
         setTimeLeft(`${days}${t("banner.days")} ${hours}${t("banner.hours")} ${minutes}${t("banner.minutes")} ${seconds}${t("banner.seconds")}`);
       } else {
         setTimeLeft(`${hours}${t("banner.hours")} ${minutes}${t("banner.minutes")} ${seconds}${t("banner.seconds")}`);
       }
     };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [settings]);

  if (!settings?.enabled || !settings?.end_time || dismissed) {
    return null;
  }

  return (
    <div className="bg-primary text-primary-foreground">
     <div className="container flex items-center justify-between py-2 text-center">
         <div className="flex-1 flex items-center justify-center gap-2 text-sm">
           <span className="font-medium">{t("banner.countdown")}:</span>
           <span className="font-display tracking-wider">{timeLeft}</span>
         </div>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 hover:bg-primary-foreground/10 rounded transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default CountdownBanner;
